import {Component, HostListener, inject, input, model, output, signal, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {toObservable, toSignal} from "@angular/core/rxjs-interop"
import {FormsModule} from "@angular/forms"
import {MatInputModule} from "@angular/material/input"
import {MatSelectModule} from "@angular/material/select"
import {MatTooltip} from "@angular/material/tooltip"
import {ActionBarComponent} from "@app/common/components/menu/actions/action-bar/action-bar.component"
import {ActionItemComponent} from "@app/common/components/menu/actions/action-item/action-item.component"
import {ActionPanelRowComponent} from "@app/common/components/menu/actions/action-panel-row/action-panel-row.component"
import {ActionPanelComponent} from "@app/common/components/menu/actions/action-panel/action-panel.component"
import {ScanRegion} from "@app/platform/models/scanning"
import {ResolutionOption, ScanParameters, TablePosition} from "@app/platform/models/scanning/types"
import {DataObjectThumbnailComponent} from "@common/components/data-object-thumbnail/data-object-thumbnail.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {ThumbnailLayout} from "@common/models/enums/thumbnail-layout"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {Enums} from "@enums"
import {Labels} from "@labels"
import {MaterialWithRangeTagsGQL} from "@platform/components/scanning/scanning-action-bar/scanning-action-bar.generated"
import {StateLabelComponent} from "@platform/components/shared/state-label/state-label.component"
import {catchError, EMPTY, from, map, merge, of, Subject, switchMap} from "rxjs"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-scanning-action-bar",
    templateUrl: "./scanning-action-bar.component.html",
    styleUrls: ["./scanning-action-bar.component.scss"],
    imports: [
        MatInputModule,
        MatSelectModule,
        FormsModule,
        ActionPanelRowComponent,
        ActionPanelComponent,
        ActionBarComponent,
        ActionItemComponent,
        StateLabelComponent,
        DataObjectThumbnailComponent,
        MatTooltip,
    ],
})
export class ScanningActionBarComponent {
    readonly $scanSettingsActionItem = viewChild.required<ActionItemComponent>("scanSettingsActionItem")
    readonly $debugActionItem = viewChild.required<ActionItemComponent>("debugActionItem")

    readonly $scanParams = model.required<ScanParameters>({alias: "scanParams"})

    readonly $scanRegion = input<ScanRegion | undefined>(undefined, {alias: "scanRegion"})

    readonly $notReady = input.required<boolean>({alias: "notReady"})
    readonly $taskInProgress = input<boolean>(undefined, {alias: "taskInProgress"})
    readonly $canStartScan = input<boolean>(undefined, {alias: "canStartScan"})
    readonly $resolutionOptions = input<ResolutionOption[]>(undefined, {alias: "resolutionOptions"})
    readonly $hasTransparencySupport = input<boolean>(undefined, {alias: "hasTransparencySupport"})

    readonly materialSelect = output<void>()
    readonly overviewScanStart = output<void>()
    readonly scanStart = output<void>()
    readonly cancel = output<void>()
    readonly tableCalibrationSkip = output<void>()
    readonly tableCalibrationStart = output<void>()
    readonly backlightCalibrationStart = output<void>()
    readonly tableMove = output<TablePosition>()
    readonly testImageCapture = output<void>()
    readonly logsClick = output<void>()

    @HostListener("window:keydown.control.shift.d", ["$event"])
    toggleDebugMenu(event: Event) {
        event.preventDefault()
        if (!this.$can().update.menu("debug")) {
            return
        }
        this.$showDebugMenu.update((v) => !v)
        if (!this.$showDebugMenu()) {
            this.$debugActionItem().$panel()?.hide()
        }
    }

    readonly permission = inject(PermissionsService)
    $can = this.permission.$to

    private readonly materialWithRangeTagsGql = inject(MaterialWithRangeTagsGQL)
    private readonly rangeTagTrigger$ = new Subject<void>()

    readonly $rangeTag = toSignal(
        merge(toObservable(this.$scanRegion), this.rangeTagTrigger$).pipe(
            switchMap(() => {
                const scanRegion = this.$scanRegion()
                if (!scanRegion?.material) return EMPTY
                return from(fetchThrowingErrors(this.materialWithRangeTagsGql)({id: scanRegion.material.id})).pipe(
                    map(({material: {tagAssignments}}) => (tagAssignments.length > 0 ? tagAssignments[0].tag : null)),
                    catchError(() => of(null)),
                )
            }),
        ),
        {initialValue: null},
    )

    refreshRangeTag(): void {
        this.rangeTagTrigger$.next()
    }
    readonly $showDebugMenu = signal(false)

    activateScanRegionSettingsPanel(): void {
        this.$scanSettingsActionItem().$active.set(true)
    }

    updateParams() {
        this.$scanParams.set({...this.$scanParams()})
    }

    protected readonly Enums = Enums
    protected readonly Labels = Labels
    protected readonly ThumbnailLayout = ThumbnailLayout
}
